import { createContext, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const { i18n } = useTranslation();

  const getInitialLanguage = () => {
    const savedLanguage = JSON.parse(localStorage.getItem("language"));
    return savedLanguage || { code: "en", name: "United States - English" };
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const [showMainModal, setShowMainModal] = useState(false);
  const [data, setData] = useState([]);
  const [groupsFormData, setGroupsFormData] = useState({
    warehouseCountryId: "",
    flightNumber: "",
    status: "",
    barcode: "",
    page: 1,
    perPage: 10,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [numbersSearchValue, setNumbersSearchValue] = useState("");
  const [flightsSearchValue, setFlightsSearchValue] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [visibleTableColumns, setVisibleTableColumns] = useState({});
  const [matchingPermission, setMatchingPermission] = useState(null);
  const [isPermission, setIsPermission] = useState({});
  const [groupsCountryId, setGroupsCountryId] = useState("");
  const [groupsIds, setGroupsIds] = useState([]);
  const [sortingConfig, setSortingConfig] = useState({
    key: "",
    order: "asc",
  });
  const [globalPeriod, setGlobalPeriod] = useState();
  const [selectedLanguage, setSelectedLanguage] = useState(
    getInitialLanguage()
  );

  const { links } = useSelector((state) => state.menu);
  const { pathname } = useLocation();

  //effects
  useEffect(() => {
    if (!searchTerm) {
      setFilteredItems(data);
      return;
    }
  }, [data, searchTerm]);

  useEffect(() => {
    const cleanedPathname = pathname.substring(1);
    const findMatchingPermission = (permissions) => {
      for (const permission of permissions) {
        if (permission?.pageUrl === cleanedPathname) {
          setMatchingPermission(permission);
          return;
        }

        if (permission?.children && Array.isArray(permission.children)) {
          findMatchingPermission(permission.children);
        }
      }
    };

    if (Array.isArray(links.permissions)) {
      findMatchingPermission(links.permissions);
    }

    if (matchingPermission && matchingPermission.permissions) {
      const permissionObject = {};

      matchingPermission.permissions.forEach((permission) => {
        const actionName = permission.action;
        permissionObject[actionName] = permission;
      });

      setIsPermission(permissionObject);
    }
  }, [pathname, links.permissions, matchingPermission]);

  useEffect(() => {
    const savedLanguage = JSON.parse(localStorage.getItem("language"));
    if (savedLanguage) {
      setSelectedLanguage(savedLanguage);
      i18n.changeLanguage(savedLanguage.code);
    }
  }, [i18n]);

  const changeLanguage = (lang) => {
    setSelectedLanguage(lang);
    i18n.changeLanguage(lang.code);
    localStorage.setItem("language", JSON.stringify(lang));
  };

  //functions
  const handleSort = (key) => {
    const order =
      sortingConfig.key === key && sortingConfig.order === "asc"
        ? "desc"
        : "asc";

    setSortingConfig({
      key,
      order,
    });

    let sortedData = [...filteredItems].sort((a, b) => {
      if (order === "asc") {
        return a[key] > b[key] ? 1 : -1;
      } else {
        return a[key] < b[key] ? 1 : -1;
      }
    });

    setFilteredItems(sortedData);
  };

  const handleSearch = (e) => {
    const searchTerm = e.target.value;
    setSearchTerm(searchTerm);

    const filteredItems = data?.filter((item) => {
      return Object.values(item).some(
        (value) =>
          typeof value === "string" &&
          value.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });

    setFilteredItems(filteredItems);
  };

  const handleVisibleColumn = (option) => {
    setVisibleTableColumns((prevvisiblecolumns) => ({
      ...prevvisiblecolumns,
      [option]: !prevvisiblecolumns[option],
    }));
  };

  const handleShowMainModal = () => {
    setShowMainModal(!showMainModal);
  };

  const handleMobileMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const isAnyColumnHidden = Object.values(visibleTableColumns).some(
    (isVisible) => !isVisible
  );

  const contextValue = {
    handleMobileMenu,
    handleSort,
    handleSearch,
    setData,
    handleVisibleColumn,
    handleShowMainModal,
    setVisibleTableColumns,
    setShowMainModal,
    setGroupsCountryId,
    setNumbersSearchValue,
    setFlightsSearchValue,
    setGroupsFormData,
    setGroupsIds,
    setGlobalPeriod,
    changeLanguage,
    groupsCountryId,
    visibleTableColumns,
    isAnyColumnHidden,
    filteredItems,
    searchTerm,
    showMainModal,
    isPermission,
    numbersSearchValue,
    flightsSearchValue,
    groupsFormData,
    groupsIds,
    menuOpen,
    globalPeriod,
    selectedLanguage,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

export const useAppContext = () => useContext(AppContext);
